import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { LessonCard } from './LessonCard';
import { ScrollableCardsGrid } from './ScrollableCardsGrid';
import type { Course as CourseType } from './types';

export function Course({ course }: { course: CourseType }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  // Pin the version to the one the tile came from. Without it the read falls
  // back to `max(version)` for the id, so a legacy course — folded onto its
  // successor's id as version 1 (D28) — would list the successor's lessons.
  const { data: lessonsData, isLoading } = api.courseLessons.useQuery({
    query: { limit: 50, version: course.version },
    path: { course_id: course.id },
  });
  const lessons = lessonsData?.data ?? [];

  return (
    <CourseWrapper>
      <HeaderWrapper>
        <CourseHeader>{course.title}</CourseHeader>
        <IconWrapper onClick={() => setIsOpen(!isOpen)}>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </IconWrapper>
      </HeaderWrapper>

      <SectionDescription>
        {course.description}{' '}
        <DetailLinkWrapper>
          <Link to="/courses/$courseSlug/" params={{ courseSlug: course.slug }}>
            {t('wbs.see-detail')}
          </Link>
        </DetailLinkWrapper>
      </SectionDescription>

      <LessonsWrapper isOpen={isOpen}>
        {isLoading ? (
          <LoadingText>{t('wbs.loading')}</LoadingText>
        ) : (
          <ScrollableCardsGrid>
            {lessons.map((lesson) => (
              <LessonCard key={lesson.id} lesson={lesson} />
            ))}
          </ScrollableCardsGrid>
        )}
      </LessonsWrapper>
    </CourseWrapper>
  );
}

const CourseHeader = styled.div`
  align-items: flex-start;
  color: var(--grey-1000);
  cursor: pointer;
  display: flex;
  font-family: var(--raleway);
  font-size: 18px;
  font-weight: 600;
  justify-content: space-between;
  margin-right: 8px;
  padding: 0px 0px 4px 4px;
`;

const CourseWrapper = styled.div`
  background: var(--bg-default);
  border: none;
  border-radius: 5px;
  box-shadow: none;
  padding: 20px 6px 8px 6px;
`;

const HeaderWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  user-select: none;
`;

const IconWrapper = styled.div`
  align-items: flex-start;
  color: var(--grey-1000);
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  font-size: 0.9em;
  justify-content: flex-end;
  margin: 0 8px 0 12px;
  transition: transform 0.2s ease;

  svg {
    stroke: var(--grey-1000);
    stroke-width: 25;
  }
`;

const LessonsWrapper = styled.div<{ isOpen: boolean }>`
  margin: 21px 0 0 4px;
  max-height: ${({ isOpen }) => (isOpen ? '1000px' : '0')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  overflow: hidden;
  transition:
    max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease;
  will-change: max-height, opacity;
`;

const LoadingText = styled.div`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 14px;
  padding: 12px 4px;
`;

const DetailLinkWrapper = styled.span`
  a {
    color: var(--wbs-red);
    text-decoration: none;
  }
`;

const SectionDescription = styled.p`
  font-family: var(--raleway);
  font-size: 15px;
  font-weight: 400;
  margin: 8px 0 16px 4px;
`;
